import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CalendarSync, Loader2, RefreshCw, Moon, AlertTriangle } from "lucide-react";

export default function BookingSync({ propertyId, icalUrl, syncedAt, syncError, attemptedAt,
                                      canEdit, onSynced }) {
  const [url, setUrl] = useState(icalUrl || "");
  const [bookings, setBookings] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/bookings", { params: { property_id: propertyId } })
      .then(({ data }) => setBookings(data))
      .catch(() => setBookings([]));
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setUrl(icalUrl || ""); }, [icalUrl]);

  const sync = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/properties/${propertyId}/bookings/sync`,
        { ical_url: url.trim(), create_turnovers: true });
      toast.success(`${data.imported} new reservation(s) · ${data.turnovers} turnover clean(s) scheduled`);
      load();
      onSynced?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not read that calendar");
    }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="booking-sync">
      {canEdit && (
        <div className="rounded-lg border border-border bg-white p-4 sm:p-5">
          <div className="flex items-center gap-2">
            <CalendarSync className="h-4 w-4 text-gold" />
            <h3 className="font-display text-base font-semibold text-royal-deep">Airbnb / VRBO calendar</h3>
          </div>
          <p className="text-xs text-muted-foreground mt-1.5">
            Paste the property's iCal export link. Every guest checkout automatically becomes a
            turnover clean on the schedule — and it re-syncs itself every night.
          </p>
          <Label className="mt-4 block text-xs uppercase tracking-wider text-muted-foreground">
            Calendar link (.ics)
          </Label>
          <div className="mt-1.5 flex flex-col sm:flex-row gap-2">
            <Input data-testid="ical-url-input" value={url} onChange={(e) => setUrl(e.target.value)}
              placeholder="https://www.airbnb.com/calendar/ical/12345.ics?s=..." className="h-11" />
            <Button data-testid="sync-bookings-btn" disabled={busy || !url.trim()} onClick={sync}
              className="h-11 gap-2 bg-gold hover:bg-gold/90 text-royal-deep font-semibold whitespace-nowrap">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Sync now
            </Button>
          </div>
          <p className="mt-2 flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <Moon className="h-3 w-3" />
            {syncedAt ? `Last synced ${new Date(syncedAt).toLocaleString()}` : "Not synced yet"}
          </p>
          {syncError && (
            <div data-testid="ical-sync-error"
                 className="mt-3 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2.5">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-600" />
              <div className="min-w-0">
                <p className="text-xs font-semibold text-red-800">Last sync failed — {syncError}</p>
                <p className="mt-0.5 text-[11px] text-red-700/90">
                  {attemptedAt ? `Tried ${new Date(attemptedAt).toLocaleString()}. ` : ""}
                  Re-copy the iCal export link from your Airbnb or Vrbo listing and sync again.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {bookings.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="bookings-empty">
          No upcoming reservations imported for this property yet.
        </p>
      ) : (
        <div className="rounded-lg border border-border bg-white divide-y divide-border" data-testid="bookings-list">
          {bookings.map((b) => (
            <div key={b._id} className="flex flex-wrap items-center gap-3 px-4 sm:px-5 py-3.5"
                 data-testid={`booking-${b._id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-royal-deep truncate">{b.summary}</p>
                <p className="text-xs text-muted-foreground">
                  {b.check_in} → {b.check_out} · {b.nights} night(s)
                </p>
              </div>
              <span className="rounded-full border border-royal/15 bg-royal/5 px-2.5 py-0.5 text-[11px] font-medium text-royal">
                Turnover {b.check_out}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
